import * as React from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useClickAway } from 'react-use';

import './styles.scss';

import { ReactComponent as BellIcon } from 'icons/bell-icon.svg';
import { ReactComponent as AccountIcon } from 'icons/account-icon-green.svg';
import { ReactComponent as Branding } from 'icons/reflections-logo.svg';
import { ReactComponent as X } from 'icons/mho-x-icon.svg';
import { ReactComponent as Hamburger } from 'icons/hamburger-menu-icon.svg';

import { UserContext } from 'context/user';
import { ActiveFacilityContext } from 'context/activeFacility';

import FlexContainer from 'global_elements/Layouts/FlexContainer';
import InlineText from 'global_elements/Text/InlineText';
import NavLinkWrapper from 'global_elements/Navigation/NavLinkWrapper';
import Button from 'global_elements/Button';
import SingleSelect from 'global_elements/Inputs/Dropdown/SingleSelect';
import PopupWindow from 'global_elements/Layouts/PopupWindow';
import Paragraph from 'global_elements/Text/Paragraph';

import { ButtonVariant } from 'global_elements/Button/variants';
import { DisplayVariant, JustifyVariant, AlignVariant } from 'global_elements/Layouts/FlexContainer/variants';
import { NavLinkVariant } from 'global_elements/Navigation/NavLinkWrapper/variants';
import { FontSizes, FontColors } from 'global_elements/Text/variants';

import { HeaderProps } from 'types/headerProps';
import { SingleHeaderDropdownStyles } from 'global_elements/Inputs/Dropdown/SingleSelect/styles';
import { AdminRoutes, AllRoutes } from 'constants/routes';
import { Roles } from 'constants/roles';
import { FacilitySelectOption } from 'types/inputProps';
import HiddenTestText from 'global_elements/Text/HiddenTestText';
import { FacilityUserTools } from 'lib/Facility/tools';
import { FacilityConst } from 'lib/Facility/facilityConsts';
import PatientUsersProxy from 'api/patientUsers/patientUsersProxy';
import MHODateTime from 'domain/dateTime/MHODateTime';
import { NotificationData } from 'interfaces/patients/notificationData';
import { useTranslation } from 'react-i18next';
import { NavBarVariant } from 'global_elements/Navigation/NavBar/variants';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useMediaQuery } from 'react-responsive';
import PrimaryHeader from 'global_elements/Text/PrimaryHeader';
import ReportsProxy from 'api/reports/reportsProxy';
import { ReportSessionModes } from 'interfaces/reports/tableauReport';
import { appTranslationKeys } from '../../i18n';

const Header = (props: HeaderProps): React.JSX.Element => {
  const { user, userAccount, Logout, isNavExpanded, SetIsNavExpanded, reportTrackingID, workBookHash } = React.useContext(UserContext);
  const { facility, SwitchFacility } = React.useContext(ActiveFacilityContext);
  const [facilityOptions, setFacilityOptions] = React.useState<FacilitySelectOption[]>([]);
  const [accountDropActive, setAccountDropActive] = React.useState(false);
  const [notificationsDropActive, setNotificationDropActive] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationData[]>([]);
  const [selectedNotification, setSelectedNotification] = React.useState<NotificationData>();
  const { t } = useTranslation('translation', { keyPrefix: 'app' });
  const history = useHistory();
  const accountRef = React.useRef(null);
  const notificationsRef = React.useRef(null);
  const notificationDetailRef = React.useRef(null);
  const isPatientRegistrationPage = useRouteMatch(`${AllRoutes.PATIENT_REGISTRATION}/:facilityID`);
  const isPatientAccountView = useRouteMatch(`${AdminRoutes.PATIENT_ACCOUNTS}/:patient`);
  const isPatientAccountSetup = useRouteMatch(`${AdminRoutes.PATIENT_ACCOUNTS}/:patient/account-setup`);
  const isLaptop = useMediaQuery({
    query: 'only screen and (max-width : 1340px)',
  });
  const isIconNav = props.navbarVariant === NavBarVariant.ICON || props.navbarVariant === NavBarVariant.ICON_EXPANDED;
  const showHamburger = isIconNav || isLaptop;

  React.useEffect(() => {
    if (userAccount && userAccount.patientID) {
      PatientUsersProxy.getPatientNotifications(
        userAccount.patientID,
        (resp) => {
          const allNotifications = resp.data ? resp.data : [];
          setNotifications(allNotifications);
        },
        (e) => console.error(e),
      );
    }
  }, [userAccount, selectedNotification]);

  React.useEffect(() => {
    if (!user || user.facilities.length <= 0) {
      return;
    }

    if (user.role === Roles.SUPER_ADMIN || user.role === Roles.ADMIN) {
      FacilityUserTools.getUserFacilityOptions()
        .then((newFacilityOptions: FacilitySelectOption[]) => {
          setFacilityOptions(newFacilityOptions);
          if (facility.value === '') {
            SwitchFacility(FacilityConst.ALL_FACILITY_OPTION);
          }
        })
        .catch((err: any) => console.log(err));
    } else {
      const showAllFacilities = user.role === Roles.SUPER_CORPORATION || (user.role === Roles.CORPORATION && user.facilities.length > 3);
      const newFacilityOptions: FacilitySelectOption[] = FacilityUserTools.getFacilityOptions(user.facilities, true, showAllFacilities);
      setFacilityOptions(newFacilityOptions);
      if (facility.value === '') {
        SwitchFacility(newFacilityOptions[0]);
      }
    }
  }, [user]);

  const ShowAccountMenu = (): void => {
    setAccountDropActive(!accountDropActive);
  };

  const ShowNotificationsMenu = (): void => {
    setNotificationDropActive(!notificationsDropActive);
  };

  const HideAccountMenu = (): void => {
    setTimeout(() => {
      if (accountDropActive) {
        setAccountDropActive(false);
      }
    }, 1);
  };

  const HideNotifcationsMenu = (): void => {
    setTimeout(() => {
      if (notificationsDropActive) {
        setNotificationDropActive(false);
      }
    }, 1);
  };

  const ToggleHamburgerMenu = (): void => {
    const nav = document.getElementById('mho-nav-bar');
    if (nav) {
      nav.classList.toggle('shown');
    }
    SetIsNavExpanded(!isNavExpanded);
  };

  const ShowSelectedNotification = (notification: NotificationData): void => {
    const updatedNotification = notification;
    updatedNotification.activatedDate = notification.activatedDate || new MHODateTime(new Date()).getDateTimeForPost();
    updatedNotification.statusID = 1;
    PatientUsersProxy.savePatientNotification(
      updatedNotification,
      (resp) => console.log(resp),
      (resp) => console.error(resp),
    );
    setSelectedNotification(notification);
  };

  useClickAway(notificationDetailRef, () => setSelectedNotification(undefined));
  useClickAway(accountRef, HideAccountMenu);
  useClickAway(notificationsRef, HideNotifcationsMenu);

  const NavToAccount = (): void => {
    history.push(AllRoutes.ACCOUNT);
    HideNotifcationsMenu();
    HideAccountMenu();
  };

  const endReportSession = (): void => {
    const facilityID = parseInt(facility.value, 10);
    if (Number.isNaN(facilityID)) {
      return;
    }
    ReportsProxy.sendUserSessionData(facilityID, workBookHash, [{ trackingID: reportTrackingID }], ReportSessionModes.END).catch((error) => {
      console.log(error);
    });
  };

  const NavToLogin = (): void => {
    if (reportTrackingID !== '' && workBookHash !== '') {
      endReportSession();
    }
    setTimeout(() => {
      Logout();
      HideNotifcationsMenu();
      HideAccountMenu();
    }, 1000); // Slight delay to allow API call to go through before user is logged out
  };

  const newNotifications = (): string => {
    let num = 0;

    for (let i = 0; i < notifications.length; i += 1) {
      if (notifications[i].newNotification) {
        num += 1;
      }
    }

    return num > 0 ? `${num}` : '';
  };

  const notificationBlock = (content: NotificationData): React.JSX.Element => (
    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="notification">
      <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.START} extraClasses="notification__clickable">
        <Button variant={ButtonVariant.INVISIBLE} onClick={() => ShowSelectedNotification(content)}>
          <InlineText text={content.notificationTitle} fontColor={FontColors.SECONDARY} fontSize={FontSizes.REGULAR} underlined />
        </Button>
        {content.newNotification && <div className="new" />}
      </FlexContainer>
      {/* {content.dateCreated
              && (
              <InlineText
                  text={new MHODateTime(content.dateCreated).getDateTimeForDisplay()}
                  fontColor={FontColors.DARK_GRAY}
                  fontSize={FontSizes.SMALL}
              />
              )} */}
    </FlexContainer>
  );

  const renderNotificationDetailPopup = (): React.JSX.Element => (
    <div ref={notificationDetailRef} className="notification-detail-wrapper">
      <Button
        variant={ButtonVariant.INVISIBLE}
        onClick={() => {
          history.push('/');
          setSelectedNotification(undefined);
        }}
        extraClasses="notification-nav-button"
      >
        <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses="notification-detail">
          <PopupWindow>
            <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="close-button">
              <Button
                variant={ButtonVariant.INVISIBLE}
                onClick={(e) => {
                  e?.preventDefault();
                  e?.stopPropagation();
                  setSelectedNotification(undefined);
                }}
              >
                <X />
              </Button>
            </FlexContainer>
            <InlineText text={selectedNotification?.notificationTitle || ''} fontColor={FontColors.PRIMARY} fontSize={FontSizes.EXTRA_LARGE} />
            {/* <InlineText
                            text={new MHODateTime(selectedNotification ? selectedNotification.dateCreated : new Date()).getDateTimeForDisplay()}
                            fontColor={FontColors.DARK_GRAY}
                            fontSize={FontSizes.SMALL}
                        /> */}
            <Paragraph text={selectedNotification?.notificationMessage || ''} fontColor={FontColors.DARK} fontSize={FontSizes.LARGE} />
            <Paragraph
              // text={selectedNotification?.notificationAdditional || ''}
              text=""
              fontColor={FontColors.DARK}
              fontSize={FontSizes.LARGE}
            />
            {/* <Paragraph
                            text={selectedNotification?.notificationFooter || ''}
                            fontColor={FontColors.DARK_GRAY}
                            fontSize={FontSizes.SMALL}
                            extraClasses="footer"
                        /> */}
            <Paragraph text="Click to view available assessments" fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} extraClasses="footer underlined" />
          </PopupWindow>
        </FlexContainer>
      </Button>
    </div>
  );

  const renderAccountDropdown = (): React.JSX.Element => (
    <PopupWindow>
      <div ref={accountRef}>
        <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="header-menu account-menu">
          <Button variant={ButtonVariant.INVISIBLE} onClick={NavToAccount}>
            <InlineText text={t(appTranslationKeys.account.account)} fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} />
          </Button>
          <Button variant={ButtonVariant.INVISIBLE} onClick={NavToLogin}>
            <InlineText text={t(appTranslationKeys.account.logout)} fontColor={FontColors.PRIMARY} fontSize={FontSizes.LARGE} />
          </Button>
        </FlexContainer>
      </div>
    </PopupWindow>
  );

  const renderNotificationsDropdown = (): React.JSX.Element => (
    <PopupWindow>
      <div ref={notificationsRef}>
        <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="header-menu notifications-menu">
          <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="close-button">
            <Button variant={ButtonVariant.INVISIBLE} onClick={() => setNotificationDropActive(false)}>
              <X />
            </Button>
          </FlexContainer>
          <Paragraph text={t(appTranslationKeys.notifications.notifications)} fontColor={FontColors.DARK} fontSize={FontSizes.EXTRA_LARGE} />
          <Paragraph
            text={newNotifications() ? `${newNotifications()} ${t(appTranslationKeys.notifications.newDescriptor)}` : t(appTranslationKeys.notifications.noNewDescriptor)}
            fontColor={FontColors.DARK_GRAY}
            fontSize={FontSizes.SMALL}
          />
          {notifications.map((note, index) => (
            <React.Fragment key={`${note.notificationTitle}-${note.dateCreated}`}>
              {notificationBlock(note)}
              {index < notifications.length - 1 && <hr />}
            </React.Fragment>
          ))}
        </FlexContainer>
      </div>
    </PopupWindow>
  );

  const renderAccountButton = (): React.JSX.Element => (
    <NavLinkWrapper variant={NavLinkVariant.DECORATIVE} route={AllRoutes.ACCOUNT}>
      <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="account-button-container">
        <Button variant={ButtonVariant.INVISIBLE} onClick={ShowAccountMenu}>
          <HiddenTestText text="AccountButton" />
          <AccountIcon title={t(appTranslationKeys.account.account)} />
        </Button>
        {accountDropActive && renderAccountDropdown()}
      </FlexContainer>
    </NavLinkWrapper>
  );

  const renderNotifcationsButton = (): any => userAccount
    && userAccount?.patientID && (
    <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.START} align={AlignVariant.START} extraClasses="notifications-button-container">
      {newNotifications() && (
        <FlexContainer display={DisplayVariant.FLEX_COL} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER} extraClasses="message-counter">
          <InlineText text={newNotifications()} fontColor={FontColors.BACKGROUND} fontSize={FontSizes.SMALL} />
        </FlexContainer>
      )}
      <Button variant={ButtonVariant.INVISIBLE} onClick={ShowNotificationsMenu}>
        <BellIcon title={t(appTranslationKeys.notifications.notifications)} />
      </Button>
      {notificationsDropActive && renderNotificationsDropdown()}
    </FlexContainer>
  );

  const renderHeaderRight = (): React.JSX.Element => (
    <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.SPACE_BETWEEN} align={AlignVariant.CENTER} extraClasses="header-main-content-container">
      {props.pageTitle && <PrimaryHeader text={props.pageTitle} fontColor={FontColors.BACKGROUND} extraClasses="page-title" />}

      <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.END} align={AlignVariant.CENTER} extraClasses="header-right-wrapper">
        <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.CENTER} align={AlignVariant.CENTER}>
          {user?.role !== Roles.PATIENT
            && user?.role !== Roles.ADMIN
            && user?.role !== Roles.SUPER_ADMIN
            && facility.value !== ''
            && facilityOptions.length > 0
            && !isPatientRegistrationPage
            && !isPatientAccountSetup
            && !isPatientAccountView && <SingleSelect styles={SingleHeaderDropdownStyles} defaultValue={facility} value={facility} onSelection={SwitchFacility} options={facilityOptions} />}
          {user?.role !== Roles.PATIENT
            && user?.role !== Roles.ADMIN
            && user?.role !== Roles.SUPER_ADMIN
            && facility.value !== ''
            && facilityOptions.length > 0
            && (isPatientRegistrationPage || isPatientAccountSetup || isPatientAccountView) && (
            <InlineText fontColor={FontColors.BACKGROUND} fontSize={FontSizes.EXTRA_LARGE} text={`${facility.label} (${facility.value})`} />
          )}
          <InlineText fontColor={FontColors.BACKGROUND} fontSize={FontSizes.EXTRA_LARGE} text={props.patientName} extraClasses="header-name" />
          {renderAccountButton()}
          {renderNotifcationsButton()}
        </FlexContainer>
      </FlexContainer>
    </FlexContainer>
  );

  return (
    <>
      <header>
        <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER}>
          <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses="branding-wrapper">
            <FlexContainer display={DisplayVariant.FLEX_ROW} justify={JustifyVariant.START} align={AlignVariant.CENTER} extraClasses={`branding ${!showHamburger ? '--only-logo' : ''}`}>
              {showHamburger && (
                <Button variant={ButtonVariant.INVISIBLE} onClick={ToggleHamburgerMenu}>
                  <Hamburger width="30px" height="30px" />
                </Button>
              )}

              <Branding />
            </FlexContainer>
          </FlexContainer>
          {renderHeaderRight()}
        </FlexContainer>
      </header>
      {selectedNotification && renderNotificationDetailPopup()}
    </>
  );
};

export default Header;
